import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { X, Plus, Tag, Check, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface WellTag {
  id: string;
  name: string;
  color: string;
}

interface WellTagsProps {
  wellId: string;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  onClose?: () => void;
  mode?: 'inline' | 'modal';
}

const DEFAULT_TAGS: WellTag[] = [
  { id: 'high-priority', name: 'High Priority', color: 'bg-red-500' },
  { id: 'new', name: 'New', color: 'bg-green-500' },
  { id: 'needs-attention', name: 'Needs Attention', color: 'bg-orange-500' },
  { id: 'maintenance', name: 'Maintenance', color: 'bg-yellow-500' },
  { id: 'low-producer', name: 'Low Producer', color: 'bg-gray-500' },
  { id: 'high-producer', name: 'High Producer', color: 'bg-blue-500' },
  { id: 'water-issues', name: 'Water Issues', color: 'bg-cyan-500' },
  { id: 'gas-issues', name: 'Gas Issues', color: 'bg-purple-500' },
  { id: 'pumping-issues', name: 'Pumping Issues', color: 'bg-pink-500' },
  { id: 'scheduled-workover', name: 'Scheduled Workover', color: 'bg-indigo-500' },
];

const TAG_COLORS = [
  'bg-red-500',
  'bg-orange-500',
  'bg-yellow-500',
  'bg-green-500',
  'bg-teal-500',
  'bg-blue-500',
  'bg-indigo-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-gray-500',
];

export default function WellTags({ wellId, selectedTags, onTagsChange, onClose, mode = 'modal' }: WellTagsProps) {
  const [availableTags, setAvailableTags] = useState<WellTag[]>([]);
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState(TAG_COLORS[0]);
  const [editingTag, setEditingTag] = useState<string | null>(null);

  useEffect(() => {
    // Load tags from storage
    const stored = localStorage.getItem('well-tags');
    if (stored) {
      setAvailableTags(JSON.parse(stored));
    } else {
      setAvailableTags(DEFAULT_TAGS);
      localStorage.setItem('well-tags', JSON.stringify(DEFAULT_TAGS));
    }
  }, []);

  const handleToggleTag = (tagId: string) => {
    const newTags = selectedTags.includes(tagId)
      ? selectedTags.filter(t => t !== tagId)
      : [...selectedTags, tagId];
    
    onTagsChange(newTags);
    
    const tag = availableTags.find(t => t.id === tagId);
    if (tag) {
      if (newTags.includes(tagId)) {
        toast.success(`Added tag: ${tag.name}`);
      } else {
        toast.success(`Removed tag: ${tag.name}`);
      }
    }
  };

  const handleCreateTag = () => {
    if (!newTagName.trim()) {
      toast.error('Please enter a tag name');
      return;
    }

    const newTag: WellTag = {
      id: `tag-${Date.now()}`,
      name: newTagName.trim(),
      color: selectedColor,
    };

    const updatedTags = [...availableTags, newTag];
    setAvailableTags(updatedTags);
    localStorage.setItem('well-tags', JSON.stringify(updatedTags));
    
    // Auto-select the new tag
    onTagsChange([...selectedTags, newTag.id]);
    
    setNewTagName('');
    setShowNewTag(false);
    setSelectedColor(TAG_COLORS[0]);
    toast.success(`Created tag: ${newTag.name}`);
  };

  const handleDeleteTag = (tagId: string) => {
    const tag = availableTags.find(t => t.id === tagId);
    if (!tag) return;

    if (confirm(`Delete tag "${tag.name}"? This will remove it from all wells.`)) {
      const updatedTags = availableTags.filter(t => t.id !== tagId);
      setAvailableTags(updatedTags);
      localStorage.setItem('well-tags', JSON.stringify(updatedTags));
      
      // Remove from selected tags
      if (selectedTags.includes(tagId)) {
        onTagsChange(selectedTags.filter(t => t !== tagId));
      }
      
      toast.success('Tag deleted');
    }
  };

  const content = (
    <div className="space-y-4">
      {/* Selected Tags Summary */}
      {selectedTags.length > 0 && mode === 'modal' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs font-medium text-blue-900 mb-2">Active Tags:</p>
          <div className="flex flex-wrap gap-2">
            {selectedTags.map(tagId => {
              const tag = availableTags.find(t => t.id === tagId);
              if (!tag) return null;
              return (
                <Badge
                  key={tagId}
                  className={`${tag.color} text-white border-0`}
                >
                  {tag.name}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Tags */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-semibold text-gray-900">
            {mode === 'modal' ? 'Select Tags' : 'Tags'}
          </label>
          <Button
            onClick={() => setShowNewTag(true)}
            variant="outline"
            size="sm"
            className="h-8"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Tag
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {availableTags.map(tag => {
            const isSelected = selectedTags.includes(tag.id);
            return (
              <button
                key={tag.id}
                onClick={() => handleToggleTag(tag.id)}
                className={`relative flex items-center space-x-2 p-3 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:bg-gray-50 active:bg-gray-100'
                }`}
              >
                <div className={`w-3 h-3 rounded-full ${tag.color}`}></div>
                <span className="text-sm font-medium text-gray-900 flex-1 text-left">
                  {tag.name}
                </span>
                {isSelected && (
                  <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                )}
                
                {/* Delete button for custom tags */}
                {!DEFAULT_TAGS.find(t => t.id === tag.id) && mode === 'modal' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTag(tag.id);
                    }}
                    className="absolute top-1 right-1 p-1 rounded bg-red-500 text-white opacity-0 hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* New Tag Form */}
      {showNewTag && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900">Create New Tag</h4>
            <button
              onClick={() => {
                setShowNewTag(false);
                setNewTagName('');
                setSelectedColor(TAG_COLORS[0]);
              }}
              className="p-1 rounded hover:bg-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Tag Name
              </label>
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="e.g., Needs Inspection"
                className="h-10"
                maxLength={30}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Color
              </label>
              <div className="flex flex-wrap gap-2">
                {TAG_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={`w-10 h-10 rounded-lg ${color} transition-transform ${
                      selectedColor === color ? 'ring-2 ring-blue-600 ring-offset-2 scale-110' : ''
                    }`}
                  ></button>
                ))}
              </div>
            </div>

            <Button
              onClick={handleCreateTag}
              className="w-full h-10 bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Tag
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  if (mode === 'inline') {
    return content;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center space-x-2">
              <Tag className="h-5 w-5" />
              <span>Well Tags</span>
            </h3>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <p className="text-sm text-purple-100 mt-1">
            Organize and categorize your wells
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {content}
        </div>

        {/* Footer */}
        {onClose && (
          <div className="border-t p-4">
            <Button
              onClick={onClose}
              className="w-full h-12 bg-purple-600 hover:bg-purple-700"
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Inline Tag Display Component (for showing tags on well cards)
export function WellTagBadges({ tags }: { tags: string[] }) {
  const [availableTags, setAvailableTags] = useState<WellTag[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem('well-tags');
    if (stored) {
      setAvailableTags(JSON.parse(stored));
    } else {
      setAvailableTags(DEFAULT_TAGS);
    }
  }, []);

  if (tags.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.slice(0, 3).map(tagId => {
        const tag = availableTags.find(t => t.id === tagId);
        if (!tag) return null;
        return (
          <Badge
            key={tagId}
            className={`${tag.color} text-white border-0 text-xs px-2 py-0.5`}
          >
            {tag.name}
          </Badge>
        );
      })}
      {tags.length > 3 && (
        <Badge variant="outline" className="text-xs px-2 py-0.5">
          +{tags.length - 3}
        </Badge>
      )}
    </div>
  );
}
